/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Sparkles, Check, Eye, FileText, Settings } from 'lucide-react';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  draft: { label: 'Bozza', className: 'bg-gray-100 text-gray-600' },
  review: { label: 'In Revisione', className: 'bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]' },
  published: { label: 'Pubblicato', className: 'bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))]' },
};

export default function GestioneNews() {
  const {} = useAuth(); // cookies sent automatically

  const [tab, setTab] = useState('articles');

  // Topics state
  const [topics, setTopics] = useState([]);
  const [topicDialogOpen, setTopicDialogOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [topicForm, setTopicForm] = useState({ nome: '', system_prompt: '', active: true });

  // Articles state
  const [articles, setArticles] = useState([]);
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ topic_id: '', titolo: '', contenuto: '', sommario: '', immagine_url: '', status: 'draft' });
  const [statusFilter, setStatusFilter] = useState('all');

  // AI generation state
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [genTopicId, setGenTopicId] = useState('');
  const [genPrompt, setGenPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  const [loading, setLoading] = useState(true);

  const activeTopics = useMemo(() => topics.filter(t => t.active), [topics]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, aRes] = await Promise.all([
        axios.get(`${API}/news/topics`, {}),
        axios.get(`${API}/news/articles`, { params: { status: statusFilter !== 'all' ? statusFilter : undefined } })
      ]);
      setTopics(tRes.data);
      setArticles(aRes.data);
    } catch (err) { logger.error('Fetch error:', err); } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Topics handlers
  const openCreateTopic = () => { setEditingTopic(null); setTopicForm({ nome: '', system_prompt: '', active: true }); setTopicDialogOpen(true); };
  const openEditTopic = (t) => { setEditingTopic(t.id); setTopicForm({ nome: t.nome, system_prompt: t.system_prompt, active: t.active }); setTopicDialogOpen(true); };
  const saveTopic = async () => {
    try {
      if (editingTopic) { await axios.put(`${API}/news/topics/${editingTopic}`, topicForm, {}); toast.success('Argomento aggiornato'); }
      else { await axios.post(`${API}/news/topics`, topicForm, {}); toast.success('Argomento creato'); }
      setTopicDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const deleteTopic = async (id) => { try { await axios.delete(`${API}/news/topics/${id}`, {}); toast.success('Eliminato'); fetchData(); } catch { toast.error('Errore'); } };

  // Articles handlers
  const openCreateArticle = () => { setEditingArticle(null); setArticleForm({ topic_id: '', titolo: '', contenuto: '', sommario: '', immagine_url: '', status: 'draft' }); setArticleDialogOpen(true); };
  const openEditArticle = (a) => { setEditingArticle(a.id); setArticleForm({ topic_id: a.topic_id || '', titolo: a.titolo, contenuto: a.contenuto, sommario: a.sommario || '', immagine_url: a.immagine_url || '', status: a.status }); setArticleDialogOpen(true); };
  const saveArticle = async () => {
    try {
      if (editingArticle) { await axios.put(`${API}/news/articles/${editingArticle}`, articleForm, {}); toast.success('Articolo aggiornato'); }
      else { await axios.post(`${API}/news/articles`, articleForm, {}); toast.success('Articolo creato'); }
      setArticleDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const deleteArticle = async (id) => { try { await axios.delete(`${API}/news/articles/${id}`, {}); toast.success('Eliminato'); fetchData(); } catch { toast.error('Errore'); } };
  const publishArticle = async (id) => {
    try { await axios.put(`${API}/news/articles/${id}`, { status: 'published' }, {}); toast.success('Pubblicato!'); fetchData(); }
    catch { toast.error('Errore'); }
  };

  // AI generation
  const handleGenerate = async () => {
    if (!genTopicId) { toast.error('Seleziona un argomento'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/news/generate`, { topic_id: genTopicId, prompt_aggiuntivo: genPrompt || null }, {});
      toast.success('Articolo generato! Vai in "In Revisione" per rivederlo.');
      setGenerateDialogOpen(false);
      setGenPrompt('');
      setStatusFilter('review');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella generazione AI');
    } finally { setGenerating(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <PageTitle title="Gestione News" info="Articoli, argomenti e generazione AI dei contenuti per la sezione News del sito." />
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setGenerateDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white">
            <Sparkles size={16} className="mr-1" /> Genera con AI
          </Button>
          <Button onClick={openCreateArticle} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">
            <Plus size={16} className="mr-1" /> Nuovo Articolo
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="articles"><FileText size={14} className="mr-1" /> Articoli ({articles.length})</TabsTrigger>
          <TabsTrigger value="topics"><Settings size={14} className="mr-1" /> Argomenti ({topics.length})</TabsTrigger>
        </TabsList>

        {/* ARTICLES TAB */}
        <TabsContent value="articles">
          <div className="flex gap-2 mb-4">
            {['all','draft','review','published'].map(s => (
              <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'} onClick={() => setStatusFilter(s)} className={statusFilter === s ? 'bg-[#238A92] text-white' : ''}>
                {s === 'all' ? 'Tutti' : STATUS_MAP[s]?.label || s}
              </Button>
            ))}
          </div>
          <Card>
            <CardContent className="p-0">
              {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div> : articles.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">Nessun articolo. Creane uno manualmente o usa la generazione AI!</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Titolo</TableHead><TableHead>Argomento</TableHead><TableHead>Stato</TableHead><TableHead>AI</TableHead><TableHead className="text-right">Azioni</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {articles.map(a => (
                        <TableRow key={a.id}>
                          <TableCell className="font-medium max-w-[300px] truncate">{a.titolo}</TableCell>
                          <TableCell>{a.topic_nome || '-'}</TableCell>
                          <TableCell><Badge className={STATUS_MAP[a.status]?.className}>{STATUS_MAP[a.status]?.label}</Badge></TableCell>
                          <TableCell>{a.generated_by_ai ? <Badge variant="secondary" className="text-[10px]">AI</Badge> : '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {a.status !== 'published' && <Button size="sm" variant="ghost" className="h-8 px-2 text-green-600" onClick={() => publishArticle(a.id)} title="Pubblica"><Check size={14} /></Button>}
                              <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => openEditArticle(a)}><Edit size={14} /></Button>
                              <AlertDialog><AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="h-8 px-2 text-red-600"><Trash2 size={14} /></Button></AlertDialogTrigger>
                                <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Elimina</AlertDialogTitle><AlertDialogDescription>Sei sicuro?</AlertDialogDescription></AlertDialogHeader>
                                  <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => deleteArticle(a.id)} className="bg-red-600">Elimina</AlertDialogAction></AlertDialogFooter>
                                </AlertDialogContent></AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TOPICS TAB */}
        <TabsContent value="topics">
          <div className="mb-4"><Button onClick={openCreateTopic} className="bg-[#238A92] hover:bg-[#0F5E66] text-white"><Plus size={16} className="mr-1" /> Nuovo Argomento</Button></div>
          <Card>
            <CardContent className="p-0">
              {topics.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">Nessun argomento. Crea il primo per iniziare a generare news con AI!</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>System Prompt</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {topics.map(t => (
                        <TableRow key={t.id}>
                          <TableCell className="font-medium">{t.nome}</TableCell>
                          <TableCell className="max-w-[300px] truncate text-sm text-muted-foreground">{t.system_prompt}</TableCell>
                          <TableCell><Badge className={t.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>{t.active ? 'Attivo' : 'Inattivo'}</Badge></TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => openEditTopic(t)}><Edit size={14} /></Button>
                              <AlertDialog><AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="h-8 px-2 text-red-600"><Trash2 size={14} /></Button></AlertDialogTrigger>
                                <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Elimina</AlertDialogTitle><AlertDialogDescription>Sei sicuro?</AlertDialogDescription></AlertDialogHeader>
                                  <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => deleteTopic(t.id)} className="bg-red-600">Elimina</AlertDialogAction></AlertDialogFooter>
                                </AlertDialogContent></AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Topic Dialog */}
      <Dialog open={topicDialogOpen} onOpenChange={setTopicDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{fontFamily:'Space Grotesk'}}>{editingTopic ? 'Modifica Argomento' : 'Nuovo Argomento'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nome argomento</Label><Input value={topicForm.nome} onChange={e => setTopicForm(p => ({...p, nome: e.target.value}))} placeholder="es. Salute e Prevenzione" /></div>
            <div className="space-y-2">
              <Label>System Prompt per la generazione AI</Label>
              <p className="text-xs text-muted-foreground">Definisci il contesto e le istruzioni per l'AI. Specifica da dove prendere le notizie, il tono, e il tipo di contenuto desiderato.</p>
              <Textarea value={topicForm.system_prompt} onChange={e => setTopicForm(p => ({...p, system_prompt: e.target.value}))} rows={6} placeholder="Sei un giornalista medico specializzato in salute e benessere. Scrivi articoli informativi basandoti sulle ultime ricerche scientifiche pubblicate su riviste mediche internazionali. Il tono deve essere professionale ma accessibile al pubblico generale." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTopicDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveTopic} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">{editingTopic ? 'Salva' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Article Dialog */}
      <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{fontFamily:'Space Grotesk'}}>{editingArticle ? 'Modifica Articolo' : 'Nuovo Articolo'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Argomento</Label>
                <select value={articleForm.topic_id} onChange={e => setArticleForm(p => ({...p, topic_id: e.target.value}))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="">Nessuno</option>
                  {topics.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Stato</Label>
                <select value={articleForm.status} onChange={e => setArticleForm(p => ({...p, status: e.target.value}))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="draft">Bozza</option>
                  <option value="review">In Revisione</option>
                  <option value="published">Pubblicato</option>
                </select>
              </div>
            </div>
            <div className="space-y-2"><Label>Titolo</Label><Input value={articleForm.titolo} onChange={e => setArticleForm(p => ({...p, titolo: e.target.value}))} /></div>
            <div className="space-y-2"><Label>Sommario</Label><Textarea value={articleForm.sommario} onChange={e => setArticleForm(p => ({...p, sommario: e.target.value}))} rows={2} placeholder="Breve riassunto dell'articolo" /></div>
            <div className="space-y-2"><Label>Contenuto</Label><Textarea value={articleForm.contenuto} onChange={e => setArticleForm(p => ({...p, contenuto: e.target.value}))} rows={10} /></div>
            <div className="space-y-2"><Label>URL Immagine</Label><Input value={articleForm.immagine_url} onChange={e => setArticleForm(p => ({...p, immagine_url: e.target.value}))} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setArticleDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveArticle} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">{editingArticle ? 'Salva' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}>
              <Sparkles size={20} className="text-purple-600" /> Genera Articolo con AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Seleziona un argomento e l'AI generer\u00e0 un articolo basandosi sul system prompt configurato. L'articolo verr\u00e0 salvato in stato "In Revisione" per la tua approvazione.</p>
            <div className="space-y-2">
              <Label>Argomento</Label>
              <select value={genTopicId} onChange={e => setGenTopicId(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Seleziona argomento...</option>
                {activeTopics.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Indicazioni aggiuntive (opzionale)</Label>
              <Textarea value={genPrompt} onChange={e => setGenPrompt(e.target.value)} rows={3} placeholder="es. Concentrati sulle novit\u00e0 riguardanti la prevenzione del melanoma..." />
            </div>
            {genTopicId && topics.find(t => t.id === genTopicId) && (
              <div className="bg-[hsl(var(--secondary))] rounded-lg p-3">
                <p className="text-xs font-medium mb-1">System prompt dell'argomento:</p>
                <p className="text-xs text-muted-foreground">{topics.find(t => t.id === genTopicId)?.system_prompt}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGenerateDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleGenerate} disabled={generating || !genTopicId} className="bg-purple-600 hover:bg-purple-700 text-white">
              {generating ? <><span className="animate-spin mr-2">\u25CB</span> Generazione in corso...</> : <><Sparkles size={14} className="mr-1" /> Genera</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
